import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';
import { CartSidebar } from '@/components/cart/CartSidebar';
import { useAuth } from '@/context/AuthContext';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { UserIcon, ShoppingBagIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { BrandLogo } from '@/components/common/BrandLogo';

export const Navbar = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { cartCount } = useCart();
  const [localCartCount, setLocalCartCount] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  // Pour les pages non-home, forcer le style scrolled immédiatement
  useEffect(() => {
    if (!isHomePage) {
      setScrolled(true);
      return;
    }

    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    // Vérifier la position initiale au chargement
    setScrolled(window.scrollY > 50);
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  // Écouter les mises à jour du panier
  useEffect(() => {
    const updateCartCount = () => {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      const total = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
      setLocalCartCount(total);
    };

    updateCartCount();

    const handleCartUpdate = (e: CustomEvent) => {
      const cart = e.detail || JSON.parse(localStorage.getItem('cart') || '[]');
      const total = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
      setLocalCartCount(total);
    };

    window.addEventListener('cartUpdated', handleCartUpdate as EventListener);
    window.addEventListener('storage', updateCartCount);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate as EventListener);
      window.removeEventListener('storage', updateCartCount);
    };
  }, []);

  // Toggle menu mobile
  const handleToggleMenu = () => {
    setIsMobileMenuOpen(prev => !prev);
  };

  // Fermer le menu
  const handleCloseMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Fermer le menu quand on clique en dehors
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;
      
      // Ne pas fermer si on clique sur le hamburger, dans le menu, ou sur les liens du menu
      if (
        target.closest('.hamburger-menu') ||
        target.closest('.nav-links') ||
        target.closest('#hamburger-menu-button') ||
        target.closest('.nav-menu-link') ||
        target.closest('.nav-menu-icon')
      ) {
        return;
      }
      
      setIsMobileMenuOpen(false);
    };

    // Délai pour éviter la fermeture immédiate
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Fermer le menu quand on change de page
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Empêcher le scroll du body et ajouter classe pour overlay
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('menu-open');
    } else {
      document.body.style.overflow = '';
      document.body.classList.remove('menu-open');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('menu-open');
    };
  }, [isMobileMenuOpen]);

  // Déterminer la classe de la navbar
  const navClassName = isHomePage 
    ? `main-nav transparent ${scrolled ? 'scrolled' : ''}`
    : 'main-nav scrolled non-home';

  return (
    <>
    <nav className={navClassName}>
      {/* Top Bar */}
      <div className="top-bar-nav">
        <div className="top-bar-content">
          <div className="top-bar-left">
            <span>{t('navbar.welcome')}</span>
          </div>
          <div className="top-bar-links">
            <Link to="/order-tracking">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" style={{ width: '16px', height: '16px', marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
              </svg>
              <span className="top-bar-link-text">{t('navbar.trackOrder')}</span>
            </Link>
            <div className="top-bar-socials" aria-label="Réseaux sociaux">
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.75A4 4 0 0 0 3.75 7.75v8.5a4 4 0 0 0 4 4h8.5a4 4 0 0 0 4-4v-8.5a4 4 0 0 0-4-4h-8.5Zm8.75 1.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 1.75a3.25 3.25 0 1 0 0 6.5 3.25 3.25 0 0 0 0-6.5Z" />
                </svg>
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13.5 21v-7h2.3l.45-3h-2.75V9.4c0-.82.4-1.62 1.68-1.62h1.3V5.2s-1.18-.2-2.3-.2c-2.35 0-3.88 1.42-3.88 4v2h-2.6v3h2.6v7h3.2Z" />
                </svg>
              </a>
              <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14.5 3h2.1c.2 1.2.9 2.3 1.9 3 .9.6 2 .9 3.1 1v2.2c-1.3 0-2.6-.3-3.8-.9v6.1a5.4 5.4 0 1 1-4.7-5.3v2.3a3.1 3.1 0 1 0 2.4 3V3Z" />
                </svg>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.2 8.4a1.9 1.9 0 1 1 0-3.8 1.9 1.9 0 0 1 0 3.8ZM4.6 9.8h3.2V20H4.6V9.8Zm5.2 0h3v1.4h.1c.4-.8 1.4-1.6 3-1.6 3.2 0 3.8 2.1 3.8 4.8V20h-3.2v-4.8c0-1.2 0-2.7-1.6-2.7s-1.9 1.3-1.9 2.6V20H9.8V9.8Z" />
                </svg>
              </a>
            </div>
            <div className="top-bar-language-switcher">
              <span className="top-bar-language-label">{t('app.language')}:</span>
              <LanguageSwitcher className="language-switcher-inline" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Nav Content */}
      <div className="nav-content">
        <div className="nav-left">
          <button 
            className={`hamburger-menu ${isMobileMenuOpen ? 'active' : ''} ${isHomePage && !scrolled ? 'hero-white' : ''}`}
            onClick={handleToggleMenu}
            aria-label={t('navbar.mobileMenu')}
            aria-expanded={isMobileMenuOpen}
            type="button"
            id="hamburger-menu-button"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="hamburger-icon" />
            ) : (
              <Bars3Icon className="hamburger-icon" />
            )}
          </button>
          <Link to="/" className="logo" onClick={handleCloseMenu} aria-label={t('homeLanding.brandLogoAria')}>
            <BrandLogo size="nav" />
          </Link>
        </div>

        <ul className={`nav-links ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          {/* Header du menu avec logo et bouton fermer */}
          <li className="mobile-menu-header">
            <Link to="/" onClick={handleCloseMenu} className="mobile-menu-logo" aria-label={t('homeLanding.brandLogoAria')}>
              <BrandLogo size="nav" />
            </Link>
            <button 
              className="mobile-menu-close-btn"
              onClick={handleCloseMenu}
              aria-label={t('navbar.closeMenu')}
              type="button"
            >
              <XMarkIcon className="close-icon" />
            </button>
          </li>

          {/* Menu Navigation - Sans icônes et sans titre "Navigation" */}
          <li>
            <Link to="/" onClick={handleCloseMenu} className="nav-menu-link">
              <span>{t('navbar.home')}</span>
            </Link>
          </li>
          <li>
            <Link to="/about" onClick={handleCloseMenu} className="nav-menu-link">
              <span>{t('navbar.about')}</span>
            </Link>
          </li>
          <li>
            <Link to="/contact" onClick={handleCloseMenu} className="nav-menu-link">
              <span>{t('navbar.contact')}</span>
            </Link>
          </li>
          
          {/* Section Compte - Retiré: lien Connexion supprimé selon les spécifications */}
        </ul>

        <div className="nav-icons">
          {user && (
            <button 
              className="nav-icon"
              onClick={() => navigate('/admin/dashboard')}
              aria-label={t('navbar.dashboard')}
              title={t('navbar.dashboard')}
            >
              <Squares2X2Icon className="nav-icon-svg" />
          </button>
          )}
          {!user && (
            <button 
              className="nav-icon"
              onClick={() => navigate('/login')}
              aria-label={t('navbar.login')}
              title={t('navbar.login')}
            >
              <UserIcon className="nav-icon-svg" />
            </button>
          )}
          <button 
            className="cart-badge relative"
            onClick={() => setIsCartOpen(true)}
            aria-label={t('navbar.openCart')}
          >
            <ShoppingBagIcon className="nav-icon-svg" />
            {(cartCount > 0 || localCartCount > 0) && (
              <span className="cart-count">{cartCount || localCartCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* Cart Sidebar */}
      <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </nav>
    {!isHomePage && <div className="navbar-bottom-spacer" aria-hidden="true" />}
    </>
  );
};
